import { useMemo, useEffect, useState } from "react";
import { Typography, Grid, TextField, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MainCard from "components/MainCard";
import ScrollX from "components/ScrollX";
import { useSelector } from "store";
import { useParams } from "react-router";
import { ColdActionCell } from "./ColdActionCell";
import { WarmActionCell } from "./WarmActionCell";
import { NoAnswerActionCell } from "./NoAnswerActionCell";
import { ColdListTable } from "./ColdListTable";
import { subscribeToListContacts, deleteListContact, updateListContact } from "store/reducers/list";
import EditableCell from "./EditableCell";
import IconButton from "components/@extended/IconButton";
import { addLeadToTargetCampaign } from "store/reducers/smartlead";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import useAuth from "hooks/useAuth";
import { subscribeToOutreachMonthly } from "store/reducers/data";

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const getNextContactDisplay = (dateString) => {
  if (!dateString || !DATE_REGEX.test(dateString)) return dateString || "N/A";
  const targetDate = new Date(dateString + "T00:00:00");
  if (isNaN(targetDate.getTime())) return dateString;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return <span style={{ fontWeight: "bold", color: "green" }}>Today</span>;
  if (diffDays === 1)
    return <span style={{ fontWeight: "bold", color: "darkorange" }}>Tomorrow</span>;
  if (diffDays > 1) return `In ${diffDays} days`;
  return <span style={{ color: "red", fontWeight: "bold" }}>{dateString} (Overdue)</span>;
};

const shortenUrl = (url) => {
  if (!url) return "";
  try {
    const { hostname, pathname } = new URL(url);
    let path = pathname.length > 15 ? pathname.slice(0, 12) + "…" : pathname;
    return hostname + path;
  } catch {
    return url.length > 20 ? url.slice(0, 20) + "…" : url;
  }
};

const CompanyNameCell = ({ value, row, listId, currentUser }) => {
  const { website, email_address, id } = row.original;
  const [isEditing, setIsEditing] = useState(false);
  const [newValue, setNewValue] = useState(value);

  const websiteDisplay = website ? shortenUrl(website) : null;

  // Campaign sets based on User
  const campaigns = {
    eeli: [
      { id: 3073550, label: "Kotisivujen päivitys PRO" },
      { id: 2788085, label: "Kotisivujen päivitys LITE-PRO" },
      { id: 2989423, label: "Uudet kotisivut" },
    ],
    joose: [
      { id: 3073570, label: "Kotisivujen päivitys 1000€ - Joose" },
      { id: 3003069, label: "Kotisivujen päivitys - Joose" },
      { id: 3003060, label: "Uudet kotisivut (puhelu) - Joose" },
    ],
  };

  const activeCampaignList =
    currentUser?.id === "fTXQTUEjtbOYtbTFCBNWRR2ZY7Q2" ? campaigns.joose : campaigns.eeli;

  useEffect(() => {
    setNewValue(value);
  }, [value]);

  const handleUpdate = async () => {
    if (newValue !== value && newValue.trim() !== "" && id) {
      try {
        await updateListContact(listId, { ...row.original, name: newValue.trim() });
      } catch (error) {
        console.error("Update failed", error);
      }
    }
    setIsEditing(false);
  };

  const handleCampaignSelect = async (campaignId) => {
    try {
      await deleteListContact(listId, id);
      await addLeadToTargetCampaign(row.original, campaignId);
    } catch (error) {
      console.error("Failed to add lead", error);
    }
  };

  const iconStyles = { fontSize: "12px" };
  const buttonStyles = { p: 0.3, minWidth: 0 };

  return (
    <div
      style={{
        minWidth: "150px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      {isEditing ? (
        <TextField
          value={newValue}
          variant="standard"
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" ? handleUpdate() : e.key === "Escape" && setIsEditing(false)
          }
          onBlur={handleUpdate}
          autoFocus
          size="small"
          sx={{
            maxWidth: "180px",
            "& .MuiInputBase-input": {
              fontWeight: "bold",
              fontSize: "0.875rem",
              color: "#013cfe",
              padding: "0px",
            },
          }}
        />
      ) : (
        <div
          onClick={() =>
            window.open(
              `https://www.google.com/search?q=${encodeURIComponent(value)}&hl=fi&gl=FI`,
              "_blank"
            )
          }
          style={{ fontWeight: "bold", cursor: "pointer", color: "#013cfe" }}
        >
          {value}
        </div>
      )}

      {website && !isEditing && (
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "#8c8c8c",
            fontSize: "0.75rem",
            display: "block",
          }}
        >
          {websiteDisplay}
        </a>
      )}

      {!isEditing && (
        <div
          style={{
            display: "flex",
            gap: "4px",
            marginTop: "6px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <IconButton
            color="error"
            size="small"
            onClick={() => deleteListContact(listId, id)}
            sx={buttonStyles}
          >
            <DeleteOutlined style={iconStyles} />
          </IconButton>

          <IconButton
            size="small"
            color="secondary"
            onClick={() => setIsEditing(true)}
            sx={{ ...buttonStyles, opacity: 0.6, "&:hover": { opacity: 1 } }}
          >
            <EditOutlined style={iconStyles} />
          </IconButton>

          {/* Render campaign triggers horizontally */}
          {email_address &&
            activeCampaignList.map((campaign) => (
              <Button
                key={campaign.id}
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleCampaignSelect(campaign.id)}
                sx={{
                  fontSize: "0.65rem",
                  padding: "1px 5px",
                  minWidth: "auto",
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {campaign.label}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
};

const LastContactCell = ({ value }) => <span style={{ whiteSpace: "nowrap" }}>{value || "-"}</span>;

const ListPage = () => {
  const { listId } = useParams();
  const { lists, listContacts } = useSelector((state) => state.list);
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [limit, setLimit] = useState(10);
  const theme = useTheme();

  useEffect(() => {
    if (listId) {
      const findList = lists.find((l) => l.id === listId);
      if (findList) {
        setList(findList);
        const unsubscribe = subscribeToListContacts(listId, limit);
        return () => unsubscribe();
      }
    }
  }, [listId, limit, lists]);

  useEffect(() => {
    const unsubscribe = subscribeToOutreachMonthly();
    return () => unsubscribe();
  }, []);

  const sortedData = useMemo(() => {
    if (!listContacts) return [];
    if (list?.type !== "warm") return listContacts;

    return [...listContacts].sort((a, b) => {
      const dateA = a.next_contact;
      const dateB = b.next_contact;
      const isAInvalid = !dateA || dateA === "N/A";
      const isBInvalid = !dateB || dateB === "N/A";
      if (isAInvalid && isBInvalid) return 0;
      if (isAInvalid) return 1;
      if (isBInvalid) return -1;
      return dateA.localeCompare(dateB);
    });
  }, [listContacts, list]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Company name",
        accessor: "name",
        Cell: ({ value, row }) => (
          <CompanyNameCell value={value} row={row} listId={listId} currentUser={user} />
        ),
      },
      { accessor: "id", className: "hide-on-mobile" },
      {
        Header: "Phone number",
        accessor: "phone_number",
        className: "hide-on-mobile",
        Cell: ({ value, row }) => (
          <EditableCell value={value} row={row} columnId="phone_number" listId={listId} />
        ),
      },
      {
        Header: "Email address",
        accessor: "email_address",
        className: "hide-on-mobile",
        Cell: ({ value, row }) => (
          <EditableCell value={value} row={row} columnId="email_address" listId={listId} />
        ),
      },
      { accessor: "website", className: "hide-on-mobile" },
    ];

    const isWarmList = list?.type === "warm";

    if (!isWarmList) {
      baseColumns.splice(5, 0, {
        Header: "Type",
        accessor: "industry",
        className: "hide-on-mobile",
      });
    }

    if (isWarmList) {
      baseColumns.splice(6, 0, {
        Header: "Last Contact",
        accessor: "last_contact",
        className: "hide-on-mobile",
        minWidth: 110,
        Cell: LastContactCell,
      });
      baseColumns.splice(7, 0, {
        Header: "Next Contact",
        accessor: "next_contact",
        className: "hide-on-mobile",
        minWidth: 110,
        Cell: ({ value, row }) => (
          <EditableCell value={value} row={row} columnId="next_contact" listId={listId} />
        ),
      });
      baseColumns.splice(8, 0, {
        Header: "Status",
        accessor: (row) => row.next_contact,
        id: "next_contact_status",
        className: "cell-center hide-on-mobile",
        minWidth: 100,
        Cell: ({ value }) => (
          <span style={{ whiteSpace: "nowrap" }}>{getNextContactDisplay(value)}</span>
        ),
      });
    }

    return baseColumns;
  }, [listId, list, theme, user]);

  return (
    <>
      <Grid item xs={12} sx={{ mb: 3 }}>
        <Typography variant="h3">{list?.name || "List"}</Typography>
      </Grid>
      <MainCard content={false}>
        <ScrollX>
          <ColdListTable
            columns={columns}
            data={sortedData}
            setLimit={setLimit}
            currentUser={user}
          />
        </ScrollX>
      </MainCard>
    </>
  );
};

export default ListPage;
